import React, { useState, useMemo } from "react";
import { FINANCIALS_COLUMNS } from "./columns";
import {
  FinancialsData,
  financialsCategoryMap
} from "../../../../utils/data/data";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import { useTable, useRowSelect, useFilters, useExpanded } from "react-table";
import PropTypes from "prop-types";
import "./checklisttable.scss";
import { formatCurrency, formatNumber } from "../../../../utils/utils";
import {
  WHITESPACE_REGEX,
  CURRENCY_FORMAT
} from "../../../../utils/constants/appConstants";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useSalesChecklistComment } from "../../../../hooks/useSalesChecklistComment";

const financialsEnumMap = {
  Turnover: "TURNOVER",
  "Cash In": "CASH_IN",
  "Cash Out": "CASH_OUT",
  "Gross Profit": "GROSS_PROFIT",
  Profit: "PROFIT",
  AR: "AR",
  "AR 60+": "AR_60_PLUS",
  "Debtor History": "DEBTOR_HISTORY",
  AP: "AP",
  "AP 60+": "AP_60_PLUS",
  "P & L inconsistencies": "PL_INCONSISTENCIES",
  "Current Ratio": "CURRENT_RATIO",
  GST: "GST",
  "Expected GST": "EXPECTED_GST",
  // PAYE: "PAYE",
  // "Wages, holiday pay and super": "WAGES",
  "Expected PAYE": "EXPECTED_PAYE",
  Liabilities: "TOTAL_LIABILITIES",
  Equity: "EQUITY",
  "Balance Sheet inconsistencies": "BALANCE_SHEET_INCONSISTENCIES"
};

const financialHoverTextMap = {
  "FT credit score": "Green > 0.8, Orange > 0.5, RED ≤ 0.5",
  "Credit Score": "Green > 600, Orange > 500, Red ≤ 500",
  "Cash In": "Red if Cash Out > than Cash In",
  "Cash Out": "Red if Cash Out > than Cash In",
  Profit: "Green > 0, Red <= 0",
  "AR 60+": "Green < 5%, Orange < 15%, Red >= 5%",
  "AP 60+": "Green < 5%, Orange < 15%, Red >= 5%",
  "Expected GST": "Green if GST < Expected GST, Otherwise red",
  Equity: "Green > 0, Red <= 0",
  GST: "Green if GST < Expected GST, Otherwise red"
};

// Map metrics to financial data
const mapFinancialsData = (baseData, metrics) => {
  const periodMap = { 12: "now_12m", 24: "m12_m24", 36: "m24_m36" };
  const result = baseData.map((row) => ({
    ...row,
    sectionEnum: financialsEnumMap[row.financials],
    hoverText: financialHoverTextMap[row.financials] || ""
  }));
  metrics?.forEach((metric) => {
    const mappedFinancials =
      financialsCategoryMap[metric.category] || metric.category;
    const row = result.find(
      (r) =>
        r.financials &&
        mappedFinancials &&
        r.financials.replace(/\s|_/g, "").toUpperCase() ===
          mappedFinancials.replace(/\s|_/g, "").toUpperCase()
    );
    if (row) {
      const period = periodMap[metric.monthsAgo];
      if (period) {
        if (row.sectionEnum !== "TOTAL_LIABILITIES") {
          row[period] = metric.value;
        }
        row[`${period}_color`] = metric.color;
        if (row.sectionEnum === "TOTAL_LIABILITIES" && metric.notes) {
          row[period] = metric.notes;
        }
      }
    }
  });
  return result;
};

const FinancialTableCell = ({ cell }) => {
  const { hoverText, sectionEnum } = cell.row.original;
  const tooltipId = `tooltip-${sectionEnum || (cell.value || "").toString().replace(WHITESPACE_REGEX, "-")}`;
  return (
    <>
      <div
        data-tooltip-id={tooltipId}
        data-tooltip-content={hoverText}
        className={hoverText ? "tooltip-enabled" : ""}
      >
        {cell.value}
      </div>
      {hoverText && (
        <ReactTooltip
          id={tooltipId}
          className="tooltipcustom tooltip-arrow-left"
          place="bottom-start"
          delayShow={300}
        />
      )}
    </>
  );
};

FinancialTableCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.any,
    row: PropTypes.shape({
      original: PropTypes.shape({
        hoverText: PropTypes.string,
        sectionEnum: PropTypes.string
      })
    })
  }).isRequired
};

const FinancialsTable = ({
  salesFinancialMetric,
  userId,
  salesAdditionalNote
}) => {
  const columns = useMemo(() => {
    const baseCols = [...FINANCIALS_COLUMNS];
    baseCols[0].Cell = FinancialTableCell;
    return baseCols;
  }, []);

  const data = useMemo(
    () =>
      mapFinancialsData(FinancialsData, salesFinancialMetric).filter(
        (row) => row.sectionEnum !== undefined
      ),
    [salesFinancialMetric]
  );

  const notesLookup = useMemo(() => {
    if (!Array.isArray(salesAdditionalNote)) return {};
    return salesAdditionalNote.reduce((acc, item) => {
      if (item.section && typeof item.note === "string") {
        acc[item.section] = item.note;
      }
      return acc;
    }, {});
  }, [salesAdditionalNote]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data }, useFilters, useExpanded, useRowSelect);

  const [expandedRows, setExpandedRows] = useState([]);
  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };
  const isRowExpanded = (rowId) => expandedRows.includes(rowId);

  const { commentData, setCommentData, originalNotesRef, updateSalesData } =
    useSalesChecklistComment({
      userId
    });

  return (
    <table
      className="table-fixed mt-2 mb-3 businesstable borderless_table bgless_table"
      {...getTableProps()}
    >
      <thead>
        {headerGroups.map((headerGroup) => {
          const { key, ...rest } = headerGroup.getHeaderGroupProps();
          return (
            <tr className="parentrow" {...rest} key={key}>
              <th />
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()} key={column.id}>
                  <span>{column.render("Header")}</span>
                </th>
              ))}
            </tr>
          );
        })}
      </thead>

      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          const isExpanded = isRowExpanded(row.id);
          const specialEnums = [
            "DEBTOR_HISTORY",
            "PL_INCONSISTENCIES",
            "BALANCE_SHEET_INCONSISTENCIES"
          ];
          const isSpecial = specialEnums.includes(row.original.sectionEnum);
          return (
            <React.Fragment key={row.id}>
              <tr className="parentrow" {...row.getRowProps()}>
                <td className="expand-td">
                  <span
                    className="expandbtn cursor-pointer"
                    onClick={() => toggleRow(row.id)}
                  >
                    {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                  </span>
                </td>
                {isSpecial ? (
                  <>
                    <td className="align-top width-180">
                      {row.original.financials}
                    </td>
                    <td colSpan={4} className="padding-4-2-0-0">
                      {columns[columns.length - 1].Cell({
                        value: row.original.fundtap_notes,
                        updateSalesData,
                        commentData,
                        setCommentData,
                        originalNotesRef,
                        rowId: row.id,
                        sectionEnum: row.original.sectionEnum,
                        noteValue: notesLookup[row.original.sectionEnum] ?? ""
                      })}
                    </td>
                  </>
                ) : (
                  row.cells.map((cell) => {
                    const cellProps = cell.getCellProps({
                      className: cell.column.className
                    });
                    const color = row.original[`${cell.column.id}_color`];
                    const value = row.original[cell.column.id];
                    if (cell.column.id === "fundtap_notes") {
                      return (
                        <td key={cellProps.key} {...cellProps}>
                          {cell.render("Cell", {
                            updateSalesData,
                            commentData,
                            setCommentData,
                            originalNotesRef,
                            rowId: row.id,
                            sectionEnum: row.original.sectionEnum,
                            noteValue:
                              notesLookup[row.original.sectionEnum] ?? ""
                          })}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={cellProps.key}
                        {...cellProps}
                        style={
                          color
                            ? { backgroundColor: color, padding: "2px 8px" }
                            : {}
                        }
                      >
                        {typeof value === "number" ? (
                          <>
                            {CURRENCY_FORMAT}
                            {formatNumber(value)}
                          </>
                        ) : (
                          cell.render("Cell")
                        )}
                      </td>
                    );
                  })
                )}
              </tr>
              {isExpanded && (
                <tr className="childrow colexpand">
                  <td colSpan={columns.length + 2}>
                    <div className="tdbody">
                      {columns.map((column) => {
                        const value = row.original[column.accessor];
                        const color = row.original[`${column.accessor}_color`];
                        return (
                          <span
                            key={column.accessor}
                            data-label={column.Header}
                            style={
                              color
                                ? {
                                    backgroundColor: color,
                                    display: "inline-block",
                                    minWidth: 80,
                                    padding: "2px 8px"
                                  }
                                : {}
                            }
                          >
                            {typeof value === "number" ? (
                              <>
                                {CURRENCY_FORMAT}
                                {formatCurrency(value)}
                              </>
                            ) : (
                              value || ""
                            )}
                            {color ? (
                              <span className="financecolor-span">{color}</span>
                            ) : null}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

FinancialsTable.propTypes = {
  salesFinancialMetric: PropTypes.array.isRequired,
  userId: PropTypes.string.isRequired,
  salesAdditionalNote: PropTypes.array
};

export default FinancialsTable;
